import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { Painel } from '../../../../components/Painel';
import { InputText } from '../../../../components/InputText';
import { ButtonSecurity } from '../../../../components/ButtonSecurity';

import { ChangeMail } from '../../../../modais/changeMail';
import { ChangePassoword } from '../../../../modais/changePassword';

import {
  ButtonDeleteAccount,
  Card,
  Confirm,
  Container,
  Divider,
  Form,
  Grid,
  Info,
  PageTitle,
  ProfileName,
  ProfileRole,
  Row2,
  Separate,
} from './styles';

import { useAuth } from '../../../../hooks/auth';
import { InputFileMedia } from '../../../../components/InputFileMedia';
import noAvatar from '../../../../assets/no-avatar.svg';
import { useModal } from '../../../../hooks/modal';
import { birthdayMask, phoneMask } from '../../../../utils/inputMasks';
import { getValidationErrors } from '../../../../utils/getValidationErrors';
import { formatDateForServer } from '../../../../utils/formatDate';
import { api } from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import { formatName } from '../../../../utils/formatName';
import { labelLevelAccess } from '../../../../utils/labelLeverAccess';
import { DeleteAccount } from '../../../../modais/deleteAccount';

interface FormProps {
  fullname: string;
  nickname: string;
  username?: string;
  phone?: string;
  birthday?: string;
}

const Profile: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { user, updateUser } = useAuth();
  const { addModal } = useModal();
  const { addToast } = useToast();

  const [formatedBirtday, setFormatedBirtday] = useState('');
  const [formatedPhone, setFormatedPhone] = useState('');

  const handleSubmit = useCallback(
    async (data: FormProps): Promise<void> => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          fullname: Yup.string().required('Compo obrigatório'),
          nickname: Yup.string().required('Compo obrigatório'),
          username: Yup.string().nullable(),
          phone: Yup.string()
            .nullable()
            .transform((value: string, o: string) => (o === '' ? null : value))
            .min(10, 'Insira o seu telefone com DDD'),
          birthday: Yup.string(),
        });

        await schema.validate(data, { abortEarly: false });

        const response = await api.put('users/profile', {
          fullname: data.fullname,
          nickname: data.nickname,
          username: data.username,
          phone: phoneMask(data?.phone, true),
          birthday: formatDateForServer(data.birthday),
        });

        updateUser(response.data);

        addToast({
          type: 'success',
          title: 'Dados do perfil atualizados com sucesso',
        });
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        if (err?.response?.data?.message === 'Username already in use.') {
          formRef.current?.setErrors({ username: 'Usuário já está em uso' });
          addToast({
            type: 'error',
            title: 'Usuário indisponível',
            description: 'Esse nome de usuário já está em uso. Escolha outro.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na atualização dos dados',
          description:
            'Houve um erro na atualização dos dados. Tente novamente mais tarde.',
        });
      }
    },
    [addToast],
  );

  useEffect(() => {
    if (user?.birthday) {
      setFormatedBirtday(birthdayMask(user.birthday));
    }
    if (user?.phone) {
      setFormatedPhone(phoneMask(user.phone));
    }
  }, [user]);

  return (
    <Painel>
      <Container>
        <PageTitle>Meu perfil</PageTitle>

        <Form
          ref={formRef}
          initialData={{
            fullname: user?.fullname,
            nickname: user?.nickname,
            username: user?.username,
          }}
          onSubmit={handleSubmit}
        >
          <Grid>
            <div>
              <Card>
                <Separate label="Foto de perfil" />
                <InputFileMedia
                  name="avatar"
                  endpoint="users/avatar"
                  formFieldName="avatar"
                  identificator="user-avatar"
                  mediaUrlKey="avatar_url"
                  defaultFile={user?.avatar_url}
                  defaultImage={noAvatar}
                />
                <Info text="Imagem de até 5mb." />
                <Divider />
                <div>
                  <ProfileName>{formatName(user?.fullname || '')}</ProfileName>
                  <ProfileRole>
                    {labelLevelAccess(user?.permissions)}
                  </ProfileRole>
                </div>
              </Card>

              <Card>
                <Separate label="Gerenciamento da conta" />

                <ButtonDeleteAccount
                  name="DeleteAccount"
                  value="Apagar conta"
                  type="button"
                  arrowRight={false}
                  onClick={() => addModal(<DeleteAccount />)}
                />
              </Card>
            </div>

            <div>
              <Card>
                <Separate label="Informações gerais" />
                <InputText name="fullname" label="Nome completo" />
                <InputText name="nickname" label="Apelido" />
                <InputText
                  name="username"
                  label="Usuário"
                  autoCapitalize="none"
                />

                <Row2>
                  <InputText
                    name="phone"
                    label="Telefone"
                    type="phone"
                    maxLength={11}
                    defaultValue={formatedPhone}
                    onBlur={() => {
                      formRef.current?.setFieldValue(
                        'phone',
                        phoneMask(formRef.current?.getFieldValue('phone')),
                      );
                    }}
                    onChange={() => {
                      formRef.current?.setFieldValue(
                        'phone',
                        formRef.current
                          ?.getFieldValue('phone')
                          .replace(/\D/g, ''),
                      );
                    }}
                  />
                  <InputText
                    name="birthday"
                    label="Data de nascimento"
                    defaultValue={formatedBirtday}
                    type="date"
                  />
                </Row2>

                <Confirm
                  name="submit"
                  type="submit"
                  value="Confirmar alterações"
                />
              </Card>

              <Card>
                <Separate label="Segurança" />

                <ButtonSecurity
                  name="changeEmail"
                  value="Alterar email"
                  type="button"
                  onClick={() =>
                    addModal(
                      <ChangeMail
                        fullname={formatName(user.fullname)}
                        avatar_url={user.avatar_url}
                        email={user.email}
                      />,
                    )
                  }
                />
                <ButtonSecurity
                  name="changePassword"
                  value="Alterar senha"
                  type="button"
                  onClick={() =>
                    addModal(
                      <ChangePassoword
                        fullname={formatName(user.fullname)}
                        avatar_url={user.avatar_url}
                      />,
                    )
                  }
                />
              </Card>
            </div>
          </Grid>
        </Form>
      </Container>
    </Painel>
  );
};

export { Profile };
